import { logger } from '@/services/logger';
import { parseStoreBeforeSaving } from '@/services/polotno-parser';
import { retryWithDelay } from '@/utils/retryWithDelay';
import { S3Creds, postToS3 } from '@/utils/s3';
import { resizeImage } from '@/utils/utils';
import { StoreType } from 'polotno/model/store';

const THUMBNAIL_WIDTH = 580;
const THUMBNAIL_OPTIONS = { compressFormat: 'jpeg', quality: 0.5 };

const processProjectThumbnail = async (store: StoreType): Promise<File> => {
    const pageId = store.pages[0]?.id;
    if (!pageId) throw new Error('No page ID found for thumbnail creation.');

    try {
        await store.waitLoading();
        const dataUrl = await store.toDataURL({ pageId, quickMode: true });
        const resized = await resizeImage(dataUrl, THUMBNAIL_WIDTH, THUMBNAIL_OPTIONS);
        const blob = await fetch(resized).then(res => res.blob());
        return new File([blob], 'image.jpeg', { type: 'image/jpeg' });
    } catch (error) {
        logger.error('[debouncedSaveArtifacts] Error generating project thumbnail image:', error);
        throw error;
    }
};
const processRawJson = (storeJSON = {}): File => {
    const parsed = parseStoreBeforeSaving(storeJSON);
    const stringified = JSON.stringify(parsed);
    const blob = new Blob([stringified], { type: 'application/json' });
    return new File([blob], 'raw.json');
};

export const createS3Artifacts = async (store: StoreType, storeJSON: any = {}) => {
    const rawFile = processRawJson(storeJSON);
    const thumbnailFile = await processProjectThumbnail(store);

    return { rawFile, thumbnailFile };
};

export const uploadS3Artifacts = async (
    rawCreds: S3Creds,
    thumbnailCreds: S3Creds,
    rawFile: File,
    thumbnailFile: File,
    signal?: AbortSignal
) => {
    const rawUpload = retryWithDelay({
        fn: () => postToS3(rawCreds, rawFile, signal),
        retries: 3,
        initialDelay: 1000,
        signal,
        fnLogName: 'upload project raw json',
    });
    const thumbnailUpload = retryWithDelay({
        fn: () => postToS3(thumbnailCreds, thumbnailFile, signal),
        retries: 3,
        initialDelay: 1000,
        signal,
        fnLogName: 'upload project thumbnail',
    });

    const [rawUploadResult, thumbnailUploadResult] = await Promise.all([rawUpload, thumbnailUpload]);

    if (signal?.aborted) {
        return;
    }

    if (!rawUploadResult) {
        logger.error('Failed to upload raw project file to S3');
    }

    if (!thumbnailUploadResult) {
        logger.error('Failed to upload project thumbnail file to S3');
    }
};
